import React, { useEffect } from 'react';
import { ScrollText } from 'lucide-react';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: 'By accessing or using the Electronico Holdings (Pty) Ltd website (the “Website”), you agree to be bound by these Terms of Use and all applicable laws and regulations. If you do not agree with any part of these terms, you should not use this Website.',
  },
  {
    title: '2. Use of the Website',
    body: 'This Website and its content are provided for general informational purposes about our electronic security, contract manufacturing and multimedia marketing divisions. You agree to use the Website lawfully and not to misuse, disrupt, or attempt to gain unauthorised access to any part of the Website, its servers, or connected systems.',
  },
  {
    title: '3. Disclaimer',
    body: 'The information, products and services on this Website are provided by Electronico Holdings (Pty) Ltd on an “as is” and “as available” basis. While we strive to keep all content accurate and current, we make no warranties or representations, express or implied, as to the completeness, accuracy, reliability, suitability or availability of any information, products or services contained herein. Any reliance you place on such information is strictly at your own risk. All specifications, products and services are subject to change without notice.',
  },
  {
    title: '4. Intellectual Property',
    body: 'All content on this Website — including text, graphics, logos, images and the Electronico brand — is the property of Electronico Holdings (Pty) Ltd or its content suppliers and is protected by applicable intellectual property laws. All third-party trademarks, logos and brand names referenced on this Website remain the property of their respective owners and are used for identification purposes only.',
  },
  {
    title: '5. Limitation of Liability',
    body: 'To the fullest extent permitted by law, Electronico Holdings (Pty) Ltd shall not be liable for any direct, indirect, incidental, consequential or special loss or damage arising out of, or in connection with, the use of, or inability to use, this Website or any information contained on it.',
  },
  {
    title: '6. Third-Party Links',
    body: 'This Website may contain links to external websites that are not operated by us. We have no control over, and assume no responsibility for, the content, privacy policies or practices of any third-party websites.',
  },
  {
    title: '7. Protection of Personal Information (POPIA)',
    body: 'In accordance with the Protection of Personal Information Act, 04 of 2013 (“POPIA”), Electronico Holdings (Pty) Ltd has a general legal duty to protect the personal information it processes. We are committed to ensuring the security and protection of such information and to providing a compliant and consistent approach to data protection. Any personal information you submit through this Website will be used only for the purpose for which it was provided. For any questions relating to our POPIA compliance, please contact info@electronico.co.za.',
  },
  {
    title: '8. Changes to These Terms',
    body: 'We reserve the right to amend or update these Terms of Use at any time without prior notice. Your continued use of the Website following any changes constitutes acceptance of those changes.',
  },
  {
    title: '9. Governing Law',
    body: 'These Terms of Use are governed by and construed in accordance with the laws of the Republic of South Africa, and you submit to the non-exclusive jurisdiction of the South African courts. Terms and Conditions of Trade apply.',
  },
  {
    title: '10. Contact Us',
    body: 'For any questions about these Terms of Use, please contact us at info@electronico.co.za or +27 21 020 1435, Hermanus, Western Cape, South Africa.',
  },
];

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Use \u2014 Electronico Holdings';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0a1526] text-white" data-testid="terms-page">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative pt-40 pb-16 px-5 grid-bg overflow-hidden">
          <div className="blob bg-[#0ef3fe] w-[360px] h-[360px] -top-20 -left-20 opacity-40" />
          <div className="blob bg-[#088ffe] w-[320px] h-[320px] top-10 right-0 opacity-40" />
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full neon-border px-4 py-1.5 mb-6">
              <ScrollText className="h-4 w-4 text-[#0ef3fe]" />
              <span className="font-mono-accent text-xs text-white/80">// LEGAL</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold">
              Disclaimer &amp; <span className="gradient-text">Terms of Use</span>
            </h1>
            <p className="text-white/55 mt-5 max-w-2xl mx-auto leading-relaxed">
              Please read these terms carefully before using the Electronico Holdings website.
            </p>
          </div>
        </section>

        {/* CONTENT */}
        <section className="relative pb-24 px-5">
          <div className="max-w-3xl mx-auto space-y-8">
            {SECTIONS.map((s) => (
              <div key={s.title} className="neon-border rounded-2xl p-7" data-testid={`terms-section-${s.title.split('.')[0]}`}>
                <h2 className="font-display text-xl font-bold mb-3 text-white">{s.title}</h2>
                <p className="text-sm text-white/60 leading-relaxed">{s.body}</p>
              </div>
            ))}
            <p className="text-xs text-white/35 pt-4">Last updated: {new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
